import type { ReactNode } from "react";

import { useTrialNewsLatest } from "@/hooks/useTrialNewsQuery";
import { toBackendTrialId } from "@/utils/trialIdMap";
import TrialOverviewReport from "@/views/TrialHomeScreen/components/TrialOverviewReport";
import type { InsightReportPayload } from "@/views/TrialHomeScreen/components/TrialNewsReport";

// The trial-wide executive briefing pseudo-role, not an AssumedRole. Must match
// TrialNewsDrawer's OVERVIEW_ROLE and the backend's ROLE_CHOICES key, since it
// is the query param the /trialnews endpoint filters runs by.
const OVERVIEW_ROLE = "trial-overview";

/**
 * The portfolio trial-detail Overview tab, rolled up from the Trial News
 * drawer's Trial Overview briefing.
 *
 * The briefing is the same report the in-trial drawer lands on — one row per
 * (trial, role) in trial_news_run, rendered by the shared TrialOverviewReport.
 * Callers gate this on PORT_TRIAL_SUMMARY for the individual trial; the API
 * additionally enforces TRIAL_NEWS, so a trial granted only one of the two
 * lands on `fallback` rather than a dead screen.
 */
export default function TrialSummaryView({
  trialId,
  fallback,
}: {
  trialId: string;
  fallback: ReactNode;
}) {
  const backendTrialId = toBackendTrialId(trialId);
  const { data, isLoading, isError } = useTrialNewsLatest(
    backendTrialId,
    OVERVIEW_ROLE,
    true,
  );
  const latest = data?.latest ?? null;

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center text-[13px] text-[#8C8C8C]">
        Loading trial summary…
      </div>
    );
  }

  // No briefing has been generated for this trial, or the request was refused
  // (TRIAL_NEWS ungranted) / failed. An Overview tab that renders nothing is
  // worse than the performance numbers it replaced, so show those instead.
  if (isError || !latest?.summary_markdown) {
    return <>{fallback}</>;
  }

  const generatedAt = latest.completed_at
    ? new Date(latest.completed_at).toLocaleString()
    : null;

  return (
    <div className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      <div className="flex h-[45px] items-center justify-between gap-4 border-b border-[#F0F2F4] px-[18px]">
        <span className="text-[13px] font-semibold text-[#1F2933]">
          Trial Summary
        </span>
        {generatedAt && (
          <span className="text-[12px] text-[#8AA0A9]">
            Generated {generatedAt}
          </span>
        )}
      </div>
      <div className="bg-[#FAFBFC] p-4">
        <TrialOverviewReport
          report={latest.report as InsightReportPayload | null}
          fallbackMarkdown={latest.summary_markdown}
        />
      </div>
    </div>
  );
}
